import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppLayout as CloudscapeAppLayout,
  TopNavigation,
  SideNavigation,
  Button,
  Toggle,
  Box,
  SpaceBetween,
  Modal,
  StatusIndicator,
} from '@cloudscape-design/components';
import Avatar from '@cloudscape-design/chat-components/avatar';
import { useDemoContext } from '../context/DemoContext';
import { useVersion } from '../context/VersionContext';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../context/ThemeContext';
import { useTutorial } from '../context/TutorialContext';
import StrandsLogo from '../components/StrandsLogo';
import analyticsService from '../services/analyticsService';
import resultsCache from '../services/resultsCache';

function AppLayout({ children, onShowTutorial, tools }) {
  const navigate = useNavigate();
  const [activeHref, setActiveHref] = useState('/');
  const { showV2, setShowV2 } = useVersion();
  const { resetDemo } = useDemoContext();
  const { user, isAuthEnabled, logout } = useAuth();
  const { isSummit, isDark, isTouch, toggleColorTheme, toggleVisualMode, toggleTouchMode } = useTheme();
  const { startTutorial } = useTutorial();
  const [zoomLevel, setZoomLevel] = useState(100);
  const [navigationOpen, setNavigationOpen] = useState(true);

  // Collapse the hamburger side menu whenever touch/kiosk mode is turned on
  // so the workspace has maximum room for large touch interactions.
  useEffect(() => {
    if (isTouch) {
      setNavigationOpen(false);
    }
  }, [isTouch]);

  const handleZoom = (delta) => {
    const newZoom = Math.min(200, Math.max(50, zoomLevel + delta));
    setZoomLevel(newZoom);
    document.body.style.zoom = `${newZoom}%`;
  };

  const handleNavigate = (href) => {
    setActiveHref(href);
    navigate(href);
  };

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackVoted, setFeedbackVoted] = useState(false);

  const doReset = () => {
    resetDemo();
    resultsCache.clear();
    sessionStorage.removeItem('analysisLogs');
    sessionStorage.removeItem('processingComplete');
    sessionStorage.removeItem('dm_analytics_session'); // new session ID on next event
    navigate('/');
  };

  const handleReset = () => {
    setFeedbackVoted(false);
    setShowFeedbackModal(true);
  };

  const handleFeedbackVote = (rating) => {
    analyticsService.trackFeedback(rating, 'reset_demo');
    setFeedbackVoted(true);
    setTimeout(() => {
      setShowFeedbackModal(false);
      doReset();
    }, 1200);
  };

  return (
    <>
      {/* CSAT feedback modal — shown when Reset Demo is clicked */}
      <Modal
        visible={showFeedbackModal}
        onDismiss={() => { setShowFeedbackModal(false); doReset(); }}
        header="Before you go..."
        footer={
          <Box float="right">
            <Button variant="link" onClick={() => { setShowFeedbackModal(false); doReset(); }}>
              Skip
            </Button>
          </Box>
        }
      >
        <Box textAlign="center" padding="l">
          {!feedbackVoted ? (
            <SpaceBetween size="m">
              <Box variant="p" textAlign="center">Was this demo helpful?</Box>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                <Button iconName="thumbs-up" onClick={() => handleFeedbackVote('up')}>
                  Helpful
                </Button>
                <Button iconName="thumbs-down" onClick={() => handleFeedbackVote('down')}>
                  Not helpful
                </Button>
              </div>
            </SpaceBetween>
          ) : (
            <StatusIndicator type="success">Thanks for your feedback!</StatusIndicator>
          )}
        </Box>
      </Modal>

      <TopNavigation
        identity={{
          href: '/',
          title: (
            <Box display="flex" alignItems="center">
              <span style={{ marginRight: '16px', fontSize: '0.75rem', lineHeight: '1', verticalAlign: 'super' }}>powered by</span>
              <StrandsLogo width={100} height={29} />
            </Box>
          ),
          logo: {
            alt: 'ResearchAI',
            src: '/images/logo/typeface_colour_blue_transparent.png'
          }
        }}
        utilities={[
          // Welcome message with user's name
          ...(isAuthEnabled && user ? [{
            type: 'button',
            text: `Welcome, ${user.displayName || user.firstName || 'User'}`,
            disableUtilityCollapse: false
          }] : []),
          {
            type: 'menu-dropdown',
            iconName: 'settings',
            ariaLabel: 'Settings',
            onItemClick: ({ detail }) => {
              if (detail.id === 'summit-theme') {
                toggleColorTheme();
              } else if (detail.id === 'touch-mode') {
                toggleTouchMode();
              } else if (detail.id === 'logout') {
                logout();
              } else if (detail.id === 'admin-analytics') {
                navigate('/admin/analytics');
              }
            },
            items: [
              ...(isAuthEnabled && user ? [{
                id: 'user-info',
                text: `Signed in as ${user.email || user.signInDetails?.loginId || user.username || 'User'}`,
                disabled: true
              }] : []),
              {
                id: 'summit-theme',
                text: 'Summit 2026 Theme',
                iconName: isSummit ? 'check' : undefined,
                description: isSummit ? 'On' : 'Off'
              },
              {
                id: 'touch-mode',
                text: 'Touch / Kiosk Mode',
                iconName: isTouch ? 'check' : undefined,
                description: isTouch ? 'On — optimised for large touch screens' : 'Off'
              },
              ...(isAuthEnabled && user ? [{
                id: 'logout',
                text: 'Sign out'
              }] : []),
              { id: 'divider-1', text: '-', disabled: true },
              {
                id: 'admin-analytics',
                text: '📊 Admin Dashboard',
                href: '/admin/analytics'
              }
            ]
          },
          {
            type: 'button',
            iconSvg: (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" focusable="false">
                <path d="M8 1v1M8 14v1M1 8h1M14 8h1M3.05 3.05l.7.7M12.25 12.25l.7.7M3.05 12.95l.7-.7M12.25 3.75l.7-.7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                <path d="M8 4.5a3.5 3.5 0 010 7V4.5z" fill="currentColor"/>
                <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
              </svg>
            ),
            ariaLabel: isDark ? 'Switch to light mode' : 'Switch to dark mode',
            text: '',
            onClick: toggleVisualMode
          },
          {
            type: 'button',
            text: 'Reset Demo',
            iconName: 'refresh',
            onClick: handleReset
          },
          {
            type: 'button',
            text: 'Guided Demo',
            iconName: 'suggestions',
            ariaLabel: 'Start guided demo tour',
            onClick: startTutorial
          },
          {
            type: 'button',
            iconName: 'zoom-out',
            ariaLabel: 'Zoom out',
            text: '',
            onClick: () => handleZoom(-10)
          },
          {
            type: 'button',
            text: `${zoomLevel}%`,
            ariaLabel: 'Reset zoom',
            onClick: () => { setZoomLevel(100); document.body.style.zoom = '100%'; }
          },
          {
            type: 'button',
            iconName: 'zoom-in',
            ariaLabel: 'Zoom in',
            text: '',
            onClick: () => handleZoom(10)
          }
        ]}
      />
      <CloudscapeAppLayout
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        navigation={
          <SideNavigation
            activeHref={activeHref}
            header={{ 
              href: '/'
            }}
            onFollow={e => {
              e.preventDefault();
              handleNavigate(e.detail.href);
            }}
            items={[
              {
                type: 'link',
                text: 'Home',
                href: '/'
              },
              ...(showV2 ? [
                {
                  type: 'link',
                  text: 'Process',
                  href: '/v2/process'
                },
                {
                  type: 'link',
                  text: 'Review',
                  href: '/v2/review'
                }
              ] : [
                {
                  type: 'link',
                  text: 'Select Document',
                  href: '/upload'
                },
                {
                  type: 'link',
                  text: 'Manual Analysis',
                  href: '/manual-analysis'
                },
                {
                  type: 'link',
                  text: 'AI Analysis',
                  href: '/analysis'
                }
              ]),
              {
                type: 'section',
                text: 'Deep Dive',
                items: [
                  {
                    type: 'link',
                    text: 'Strands Agents',
                    href: '/deep-dive/strands-agents'
                  },
                  {
                    type: 'link',
                    text: 'Architecture',
                    href: '/deep-dive/architecture'
                  },
                  {
                    type: 'link',
                    text: 'Deployment',
                    href: '/deep-dive/deployment'
                  },
                  {
                    type: 'link',
                    text: 'Observability',
                    href: '/deep-dive/observability'
                  },
                  {
                    type: 'link',
                    text: 'Code',
                    href: '/deep-dive/code'
                  },
                  {
                    type: 'link',
                    text: 'Model Strategy',
                    href: '/deep-dive/model-strategy'
                  },
                  {
                    type: 'link',
                    text: 'Build vs Buy',
                    href: '/deep-dive/build-vs-buy'
                  }
                ]
              }
            ]}
          />
        }
        content={children}
        contentType="default"
        tools={tools}
        toolsHide={!tools}
      />
    </>
  );
}

export default AppLayout;
